import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from '@betty/toolkit/state';
import { setCoinBalance } from '@betty/wallet/state';
import { useTranslation } from '@betty/i18n';
import { BettyWheel } from '@betty/wheel';
import { BettyWheelExposedProps } from '@betty/wheel';
import { Box, Lottie, Skeleton, Text } from '@betty/moonlight';
import { PendingReward } from './InstantWheel.types';
import {
  fetchSpinsCount,
  setRemainingSpinsCount,
  selectRemainingSpinsCount,
  selectRemainingSpinsCountState,
} from '../../state';
import InstantWheelService from '../../services/InstantWheelService/InstantWheelService';

function InstantWheel() {
  const { t } = useTranslation('instantWheel');
  const dispatch = useDispatch();
  const spinsCount = useSelector(selectRemainingSpinsCount);
  const spinsCountState = useSelector(selectRemainingSpinsCountState);
  const pendingReward = useRef<PendingReward>();
  const bettyWheel = useRef<BettyWheelExposedProps>(null);

  const update = useCallback(() => {
    if (pendingReward.current) {
      const { balance, remainingSpins } = pendingReward.current;

      dispatch(setCoinBalance(balance));
      dispatch(setRemainingSpinsCount(remainingSpins));

      pendingReward.current = undefined;
    }
  }, [dispatch]);

  const getSectors = useCallback(
    async () => (await InstantWheelService.getSectors()).dailyWheelSectors,
    [],
  );

  const getReward = useCallback(async () => {
    const reward = await InstantWheelService.getReward();

    pendingReward.current = reward;

    return reward;
  }, []);

  const handleWin = useCallback(() => {
    Lottie.present({
      animation: 'goldenShower',
      resizeMode: 'cover',
      onAnimationFinish: () => {
        update();

        bettyWheel.current?.reset();
      },
    });
  }, [update]);

  useEffect(() => {
    dispatch(fetchSpinsCount());

    return update;
  }, [update, dispatch]);

  useEffect(() => {
    Lottie.preload('goldenShower');
  }, []);

  return (
    <Box gap="space-400">
      {spinsCountState === 'pending' ? (
        <Skeleton
          width={200}
          height={30}
          alignSelf="center"
          backgroundColor="mutedBackground"
        />
      ) : (
        <Text variant="h5" color="darkSurfacePrimary" textAlign="center">
          {t('instantWheelTitle', { count: spinsCount })}
        </Text>
      )}
      <Text
        variant="body-s"
        locNS="instantWheel"
        locKey="instantWheelDescription"
        color="darkSurfacePrimary"
        textAlign="center"
      />
      <BettyWheel
        ref={bettyWheel}
        getSectors={getSectors}
        getReward={getReward}
        handleWin={handleWin}
      />
    </Box>
  );
}

export default memo(InstantWheel);
